import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';

// Determine API base URL:
// - If REACT_APP_BACKEND_URL is set and non-empty, use it (for cross-origin or explicit config)
// - Otherwise, use relative '/api' path (for same-origin deployment via nginx proxy)
const getApiBaseUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL;
  
  // If env var is set and non-empty, use it
  if (envUrl && envUrl.trim() !== '') {
    // Ensure we append /api if not already present
    const baseUrl = envUrl.trim();
    if (baseUrl.endsWith('/api')) {
      return baseUrl;
    }
    return `${baseUrl}/api`;
  }
  
  // Default: same-origin relative path (nginx proxies /api to backend)
  return '/api';
};

const API_URL = getApiBaseUrl();
const CSRF_COOKIE_NAME = 'csrf_token';
const CSRF_HEADER_NAME = 'X-CSRF-Token';

const getCookieValue = (name) => {
  const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escapedName}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  const axiosInstance = useMemo(() => {
    return axios.create({
      baseURL: API_URL,
      withCredentials: true,
    });
  }, []);

  useEffect(() => {
    const interceptorId = axiosInstance.interceptors.request.use((config) => {
      const method = (config.method || 'get').toUpperCase();
      if (["POST", "PUT", "PATCH", "DELETE"].includes(method)) {
        const csrfToken = getCookieValue(CSRF_COOKIE_NAME);
        if (csrfToken) {
          config.headers[CSRF_HEADER_NAME] = csrfToken;
        }
      }
      return config;
    });

    return () => {
      axiosInstance.interceptors.request.eject(interceptorId);
    };
  }, [axiosInstance]);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axiosInstance.get('/auth/me');
      setUser(response.data);
      setMustChangePassword(response.data.must_change_password || false);
      return response.data;
    } catch (error) {
      setUser(null);
      setMustChangePassword(false);
      return null;
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Password-based login
  const login = async (email, password) => {
    const response = await axiosInstance.post('/auth/login', { email, password });
    setUser(response.data.user);
    setMustChangePassword(response.data.must_change_password || false);
    return response.data;
  };

  // Change password
  const changePassword = async (currentPassword, newPassword) => {
    const response = await axiosInstance.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    setMustChangePassword(false);
    return response.data;
  };

  const logout = async () => {
    try {
      await axiosInstance.post('/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setMustChangePassword(false);
    }
  };

  const hasRole = (role) => {
    return user?.roles?.includes(role) || false;
  };

  const isAdmin = () => hasRole('admin') || hasRole('super_admin');
  const isSuperAdmin = () => hasRole('super_admin');
  const isManager = () => hasRole('manager') || hasRole('admin') || hasRole('super_admin');
  const isEmployee = () => hasRole('employee');

  const value = {
    user,
    loading,
    mustChangePassword,
    axiosInstance,
    login,
    changePassword,
    logout,
    fetchUser,
    hasRole,
    isAdmin,
    isSuperAdmin,
    isManager,
    isEmployee,
    API_URL,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
